import { AsyncPipe, CurrencyPipe, DecimalPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { RxFor } from '@rx-angular/template/for';
import { DepotFacade } from '@zero/depot/domain';
import { SavingsPlanFacade } from '@zero/saving-plans/domain';
import { DividendReInvestHintComponent } from '@zero/shared/feature-dividend-re-invest/web';
import { expandHeight, expandTdDivsAnimation, fadeInAnimation, onlyEnter } from '@zero/shared/ui-animations';
import { BlinkDirective } from '@zero/shared/ui-directives';
import { BigNumbersPipe, EndsWithPipe, NotesSafeHtmlPipe } from '@zero/shared/ui-pipes';
import { PushQuoteService } from '@zero/shared/ui-push';
import {
  PositionFilter,
  PositionFilterComponent,
  SortHelper,
  SorterLabelComponent,
  UserMessagesComponent,
} from '@zero/shared/ui-web-components';
import { ListToggleComponent } from '@zero/shared/ui-web-design-system';
import { PositionsFacade } from '@zero/shared/util-global-state';
import {
  Asset,
  Customer,
  DividendYield,
  FilterGroup,
  GroupedPositionData,
  InstrumentType,
  PeriodData,
  Position,
  PositionFilterType,
  PositionsSettings,
  ProtectOrderModel,
  ProtectOrderParams,
  Quote,
  Sum,
} from '@zero/shared/util-models';
import { AuthService, DataService } from '@zero/shared/util-web-app-services';
import { StatisticsFacade } from '@zero/statistics/domain';
import { TagsFacade } from '@zero/tags/domain';
import { LinkTagComponent, TagsDropdownComponent } from '@zero/tags/feature-tags/web';
import { ProtectOrderComponent } from '@zero/trade/feature-trade/web';
import { commonRoutes } from 'constants/web-app-constants';
import { environment } from 'environments/environment';
import { LOCAL_STORAGE, StorageService } from 'ngx-webstorage-service';
import { BehaviorSubject, combineLatestWith, filter, firstValueFrom, map, tap } from 'rxjs';
import { PositionComponent } from '../../components/position/position.component';
import { PositionDividendComponent } from '../position-dividend/position-dividend.component';

const POSITIONS_SETTINGS_CACHE_KEY = 'positions-settings';
const ALLOWED_DIVIDEND_TYPES: InstrumentType[] = [
  InstrumentType.Share,
  InstrumentType.Etf,
  InstrumentType.Funds,
  InstrumentType.Derivative,
];

@Component({
  selector: 'home-positions',
  templateUrl: './positions.component.html',
  styleUrls: ['./positions.component.scss'],
  animations: [fadeInAnimation, expandTdDivsAnimation, expandHeight, onlyEnter],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ListToggleComponent,
    PositionFilterComponent,
    NgTemplateOutlet,
    UserMessagesComponent,
    SorterLabelComponent,
    RxFor,
    PositionComponent,
    PositionDividendComponent,
    FormsModule,
    ProtectOrderComponent,
    BlinkDirective,
    AsyncPipe,
    DecimalPipe,
    CurrencyPipe,
    TranslocoPipe,
    NotesSafeHtmlPipe,
    BigNumbersPipe,
    EndsWithPipe,
    DividendReInvestHintComponent,
    TagsDropdownComponent,
    LinkTagComponent,
  ],
})
export class PositionsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() public actionsEnabled = true;
  @Input() public positions: Position[] = [];
  @Input() public isLoading = true;

  @ViewChild('noteTextArea') public note: ElementRef<HTMLTextAreaElement>;

  public readonly positionsFacade = inject(PositionsFacade);
  public readonly savingPlanFacade = inject(SavingsPlanFacade);
  public readonly statisticsFacade = inject(StatisticsFacade);
  public readonly depotFacade = inject(DepotFacade);

  readonly #router = inject(Router);
  readonly #dataService = inject(DataService);
  readonly #pushQuoteService = inject(PushQuoteService);
  readonly #authService = inject(AuthService);
  readonly #changeDetectorRef = inject(ChangeDetectorRef);
  readonly #destroyRef = inject(DestroyRef);
  readonly #tagsFacade = inject(TagsFacade);

  public environment = environment;
  public InstrumentType = InstrumentType;
  public isPotentialCustomer = false;
  public showPositions = true;
  public sorter: SortHelper;
  public currentCustomer: Customer;
  public filteredPositions: Position[] = [];
  public groupedPositions?: GroupedPositionData;
  public sums: Sum[] = [];
  public showDividendBtn = false;
  public overrideCurrentFilter?: FilterGroup = null;

  public currentlyEditingNote = {
    isin: undefined,
    note: undefined,
  };
  public currentlyEditingEntryQuote?: string;
  public currentlyEditingProtectOrder?: ProtectOrderParams;
  public currentlyEditingTag?: string;
  public dividendYieldByIsin: Record<string, DividendYield> = {};
  public dividendPeriods: string[] = [];
  public dividendSumsByPeriod: Record<string, number> = {};

  #currentPositionFilter = new PositionFilter(PositionFilterType.ALL, true);
  #isinsByTags: string[] = [];

  readonly #positionsSettings = new BehaviorSubject<PositionsSettings | undefined>(undefined);
  public readonly positionsSettings$ = this.#positionsSettings.asObservable();

  constructor(@Inject(LOCAL_STORAGE) private readonly _storage: StorageService) {
    this.sorter = new SortHelper('assetName', true, 'positions');
  }

  public ngOnInit(): void {
    this.#listenToSettings();
    this.#listenToCustomer();
    this.#listenToPositions();
    this.#listenToDividendYields();
    this.#listenToTags();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { positions } = changes;
    positions && this.#handlePositionChange();
  }

  public ngOnDestroy(): void {
    this.#pushQuoteService.unsubscribeMulti(this.positions.map(({ isin }) => isin));
  }

  public sortBy(sortAttr: string): void {
    this.sorter.sortBy(this.filteredPositions, sortAttr);
    if (this.groupedPositions) {
      Object.values(this.groupedPositions).forEach((positions, index) => {
        this.sorter.sortBy(positions, sortAttr, index + 1);
      });
    }
  }

  public trackById(_: number, item: Position): number {
    return item.id;
  }

  public getSums = (type: string): Sum[] => this.sums.filter((sum) => sum.type === type);

  #getSum(positions: Position[]): Sum {
    const entryValue = positions.filter((pos) => this.#includeInSum(pos)).reduce((acc, curr) => acc + curr.entryValue, 0);
    const currentValue = positions.filter((pos) => this.#includeInSum(pos)).reduce((acc, curr) => acc + curr.currentValue, 0);
    const perfAbs = currentValue - entryValue;
    const perfPct = entryValue ? (perfAbs / entryValue) * 100 : 0;
    const perfTodayAbs = positions.filter((pos) => this.#includeInSum(pos)).reduce((acc, curr) => acc + curr.perfTodayAbs, 0);
    const perfTodayPct = currentValue ? (currentValue / (currentValue - perfTodayAbs) - 1) * 100 : 0;

    return {
      currentValue,
      entryValue,
      perfAbs,
      perfPct,
      perfTodayAbs,
      perfTodayPct,
    };
  }

  #calculateSums(): void {
    this.sums = [];

    if (this.groupedPositions) {
      for (const group in this.groupedPositions) {
        this.sums.push({
          type: group as FilterGroup,
          ...this.#getSum(this.groupedPositions[group] as Position[]),
        });
      }
    } else if (this.#currentPositionFilter) {
      this.sums.push({
        type: this.#currentPositionFilter.type,
        ...this.#getSum(this.filteredPositions),
      });
    }

    const type = PositionFilterType.ALL;
    !this.sums.some((sum) => sum.type === type) &&
      this.sums.push({
        type,
        ...this.#getSum(this.positions),
      });
  }

  #includeInSum(p: Position): boolean {
    return !p.endfaellig && !p.knockedOutFlag;
  }

  public filterPositions = (positionFilter: PositionFilter): void => {
    this.#currentPositionFilter = positionFilter;
    this.overrideCurrentFilter = null;
    if (this.#currentPositionFilter.type !== PositionFilterType.ALL) {
      this.#positionsSettings.next({ ...this.#positionsSettings.value, dividendVisible: false });
    }

    this.#handlePositionChange();
  };

  async #handlePositionChange(): Promise<void> {
    this.sortAndFilter();
    this.#calculateSums();

    this.showDividendBtn = this.filteredPositions.some((position) => ALLOWED_DIVIDEND_TYPES.includes(position.type));
    if (!this.showDividendBtn) {
      this.#positionsSettings.next({ ...this.#positionsSettings.value, dividendVisible: false });
    }
  }

  public sortAndFilter = async (): Promise<void> => {
    this.sorter.sortValues(this.positions);
    this.positions = [...this.positions];
    this.groupedPositions = undefined;
    const tagFilter = (position: { isin: string }) =>
      this.#isinsByTags?.length ? this.#isinsByTags.some((isin) => isin === position.isin) : true;

    switch (this.#currentPositionFilter.type) {
      case PositionFilterType.ALL:
        this.filteredPositions = [...this.positions].filter(tagFilter);
        break;
      case PositionFilterType.CATEGORIES:
        this.groupedPositions = this.positions.filter(tagFilter).reduce(
          (result, item) => ({
            ...result,
            [item.type]: [...(result[item.type] || []), item],
          }),
          {},
        );
        break;
      case PositionFilterType.SAVINGS_PLAN:
        await this.#displaySavingsPlans();
        break;
      default:
        this.filteredPositions = this.positions.filter(({ type }) => type === this.#currentPositionFilter.type).filter(tagFilter);
        break;
    }
  };

  async #displaySavingsPlans(): Promise<void> {
    const savingsPlans = await firstValueFrom(this.savingPlanFacade.savingsPlans$);
    this.filteredPositions = [];
    if (!savingsPlans?.length) {
      return;
    }

    this.filteredPositions = this.positions.filter((position) => savingsPlans.some(({ isin }) => isin === position.isin));
  }

  public toggleNotes(settings: PositionsSettings): void {
    this.#positionsSettings.next({ ...settings, notesVisible: !settings.notesVisible });
  }

  public toggleProtections(settings: PositionsSettings): void {
    this.#positionsSettings.next({
      ...settings,
      protectionsVisible: !settings.protectionsVisible,
      dividendVisible: false,
    });
  }

  public toggleDividend(settings: PositionsSettings): void {
    if (settings.dividendVisible) {
      this.overrideCurrentFilter = null;
    } else {
      this.statisticsFacade.loadDividendYield();
      this.filterPositions(new PositionFilter(PositionFilterType.ALL, true));
      this.overrideCurrentFilter = PositionFilterType.ALL;
    }
    this.#positionsSettings.next({ ...settings, dividendVisible: !settings.dividendVisible });
  }

  public editNote(isin: string, note: string): void {
    this.currentlyEditingEntryQuote = undefined;
    this.currentlyEditingProtectOrder = undefined;
    this.currentlyEditingNote = { isin, note };
    this.#changeDetectorRef.detectChanges();
    this.note.nativeElement.focus();
  }

  public editEntryQuote(isin: string): void {
    this.currentlyEditingNote = undefined;
    this.currentlyEditingProtectOrder = undefined;
    this.currentlyEditingEntryQuote = isin;
  }

  public onSubmitNote(note: string, isin: string): void {
    this.positionsFacade.updatePositionsNote(isin, note);
    this.currentlyEditingNote = { isin: undefined, note: undefined };
    this.#positionsSettings.next({
      ...this.#positionsSettings.value,
      notesVisible: true,
    });
  }

  public updateEntryQuote = (entryQuote: number): void => {
    this.positionsFacade.updatePositionEntryQuote(this.currentlyEditingEntryQuote, entryQuote);
    this.currentlyEditingEntryQuote = undefined;
  };

  #getAssetFromPosition(position: Position): Asset {
    return {
      name: position.assetName,
      isin: position.isin,
      instrumentType: position.type,
      wkn: null,
      longName: null,
      shortName: null,
      marketName: null,
      leverage: null,
      complex: null,
      knockedOutFlag: null,
      endfaellig: null,
      tradingHoursStartTime: null,
      tradingHoursStopTime: null,
      emittentShortCode: null,
      sparplan: null,
      minimumOrderValue: null,
      historicQuotes: null,
      quoteCurrencyCode: null,
      fractionalTrading: null,
    };
  }

  #getQuoteFromPosition(position: Position): Quote {
    return {
      bid: position.quote,
      ask: null,
      isin: null,
      time: null,
      bidv: null,
      askv: null,
    };
  }

  public activateProtectOrderMode(position: Position): void {
    this.currentlyEditingNote = undefined;
    this.currentlyEditingEntryQuote = undefined;
    this.currentlyEditingProtectOrder = {
      asset: this.#getAssetFromPosition(position),
      quantity: Math.floor(position.availableQuantity),
      execType: 'market',
      direction: 'buy',
      quote: this.#getQuoteFromPosition(position),
    };
  }

  public cancelProtectOrderMode(): void {
    this.currentlyEditingProtectOrder = undefined;
  }

  public async protectOrder(protectOrder: ProtectOrderModel): Promise<void> {
    if (!this.currentlyEditingProtectOrder?.asset?.isin || !this.currentlyEditingProtectOrder?.quantity) {
      throw new Error('protect order failed');
    }

    const { validity, stop } = protectOrder;
    if (!validity || !stop) {
      throw new Error('protect order failed');
    }

    const queryParams = {
      isin: this.currentlyEditingProtectOrder.asset.isin,
      quantity: this.currentlyEditingProtectOrder.quantity,
      direction: 'sell',
      execType: 'stop',
      validity: validity.toJSON(),
      stopPrice: stop,
    };

    await this.#router.navigate([commonRoutes.postlogin.buyAndSell], { queryParams });
    this.currentlyEditingProtectOrder = undefined;
  }

  public furtherOrder(): void {
    this.#router.navigate([commonRoutes.postlogin.buyAndSell]);
  }

  #setDividendSumsByPeriod(dividendYields: DividendYield[]): void {
    for (const period of this.dividendPeriods) {
      let periodData: PeriodData = null;
      const total = dividendYields.reduce((acc, dividendYield) => {
        if (!dividendYield) {
          return acc;
        }
        const { receivedPeriods, forecastedPeriods, receivedDividendTotal } = dividendYield;
        if (period === 'total') {
          periodData = receivedDividendTotal;
        } else if (period.endsWith('e')) {
          periodData = forecastedPeriods?.find((received) => received.period === period.substring(0, 4));
        } else {
          periodData = receivedPeriods?.find((received) => received.period === period);
        }

        return periodData ? acc + periodData.dividendAbsolute : acc;
      }, 0);

      this.dividendSumsByPeriod[period] = total;
    }
  }

  #listenToSettings(): void {
    this.positionsSettings$
      .pipe(
        filter((settings) => !!settings),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((settings) => this._storage.set(POSITIONS_SETTINGS_CACHE_KEY, settings));

    this.#positionsSettings.next(
      this._storage.get(POSITIONS_SETTINGS_CACHE_KEY) ?? {
        notesVisible: false,
        protectionsVisible: false,
        dividendVisible: false,
      },
    );
  }

  #listenToCustomer() {
    this.#authService
      .isPotentialCustomer()
      .pipe(
        tap((isPotentialCustomer) => (this.isPotentialCustomer = isPotentialCustomer)),
        filter((isPotentialCustomer) => !isPotentialCustomer),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        this.#handlePositionChange();
        this.#loadCustomer();
        this.savingPlanFacade.loadSavingsPlansOverview(true);
      });
  }

  #listenToPositions() {
    this.positionsFacade.positionsInitialLoaded$
      .pipe(
        filter((positionsInitialLoaded) => positionsInitialLoaded),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        this.positionsFacade.loadPositionNotes(true);
      });
  }

  #listenToTags() {
    this.positionsFacade.positionsInitialLoaded$
      .pipe(
        filter((positionsInitialLoaded) => positionsInitialLoaded),
        combineLatestWith(this.#tagsFacade.tags$, this.#tagsFacade.activatedTagIds$),
        map(([_, tags, activatedTagIds]) => ({ tags, activatedTagIds })),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(({ tags, activatedTagIds }) => {
        this.#isinsByTags = activatedTagIds
          ? activatedTagIds.flatMap((activatedTagId) => {
              const relevantTag = tags?.find(({ tagId }) => tagId === activatedTagId);
              return relevantTag?.entities?.length ? relevantTag.entities.map(({ entityId }) => entityId) : [];
            })
          : [];

        this.sortAndFilter();
        this.#calculateSums();
      });
  }

  #listenToDividendYields() {
    this.statisticsFacade.dividendYields$
      .pipe(
        filter((dividendYields) => !!dividendYields),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((dividendYields) => {
        const currentYear = new Date().getFullYear();

        this.dividendPeriods = [
          `${currentYear - 2}`,
          `${currentYear - 1}`,
          `${currentYear}`,
          'total',
          `${currentYear}e`,
          `${currentYear + 1}e`,
          `${currentYear + 2}e`,
        ];
        this.#setDividendSumsByPeriod(dividendYields);

        this.dividendYieldByIsin = dividendYields
          .filter((dividendYield) => !!dividendYield?.isin)
          .reduce((acc, dividendYield) => {
            acc[dividendYield.isin] = dividendYield;
            return acc;
          }, {});
      });
  }

  #loadCustomer(): void {
    this.currentCustomer = this.#dataService.getSelectedCustomerItem();
  }
}
